import { GridColDef } from '@mui/x-data-grid';
import { RslDimension } from '../../../api/schema';
import { formatters } from '@utils/formattersV2';
import { MISSING_VALUE } from '@constants/stats';
import {
  OptimizationSelectProps,
  Option as BaseOption,
} from '@features/campaigns/components/RealTimeOptimization/ComparisonByDimension/OptimizationSelectV2';
import { sanitizeOptimization } from '@utils/valueTransformers';
import { OptimizationIndexChip } from '@features/campaigns/components/OptimizationIndexChip';
import { OptimizationSelectV2 } from '@features/campaigns/components/RealTimeOptimization/ComparisonByDimension/OptimizationSelectV2';
import { sharedColumns } from './shared-columns';
import { liftWithConfidenceText } from '@features/campaigns/domain/text/lift-with-confidence-text';

export type OptimizationIndex = keyof Pick<
  RslDimension,
  | 'salesLiftIndex'
  | 'projectedActualTransactionValuePerImpressionIndex'
  | 'projectedIncrementalTransactionValuePerImpressionIndex'
>;

type Option = BaseOption & {
  value: OptimizationIndex;
};

const optimizationIndexOptions: Record<Option['value'], Option> = {
  salesLiftIndex: {
    value: 'salesLiftIndex',
    name: 'Opt. Index - Incr. Sales Lift',
  },
  projectedIncrementalTransactionValuePerImpressionIndex: {
    value: 'projectedIncrementalTransactionValuePerImpressionIndex',
    name: 'Opt. Index - Incr. Sales per Impression',
  },
  projectedActualTransactionValuePerImpressionIndex: {
    value: 'projectedActualTransactionValuePerImpressionIndex',
    name: 'Opt. Index - Total Sales per Impression',
  },
};

type ColumnsOptions = {
  optimizationIndex: OptimizationIndex;
  setOptimizationIndex: OptimizationSelectProps<Option>['setSelectedOptimization'];
};

export const buildSalesColumns = (
  options: ColumnsOptions
): GridColDef<RslDimension>[] => [
  sharedColumns.statisticallySignificant,
  sharedColumns.dimensionValue,
  {
    field: 'optimizationIndex',
    headerName: 'Optimization Index',
    display: 'flex',
    flex: 1,
    minWidth: 400,
    align: 'center',
    headerAlign: 'center',
    valueGetter: (_, row) =>
      sanitizeOptimization(row[options.optimizationIndex]),
    renderHeader: () => (
      <OptimizationSelectV2
        selectedOptimization={
          optimizationIndexOptions[options.optimizationIndex]
        }
        setSelectedOptimization={options.setOptimizationIndex}
        options={Object.values(optimizationIndexOptions)}
      />
    ),
    renderCell: ({ row }) => (
      <OptimizationIndexChip
        value={sanitizeOptimization(row[options.optimizationIndex])}
      />
    ),
  },
  sharedColumns.impressions,
  sharedColumns.uniqueUsers,
  {
    field: 'totalProjectedSales',
    headerName: 'Total Projected Sales',
    display: 'flex',
    flex: 1,
    minWidth: 150,
    type: 'number',
    valueFormatter: value => formatters.currency(value, { decimals: 1 }),
  },
  {
    field: 'incrementalSalesLift',
    headerName: 'Incr. Sales Lift',
    display: 'flex',
    flex: 1.2,
    minWidth: 220,
    type: 'number',
    valueFormatter: (_, row) =>
      row.statisticallySignificant
        ? liftWithConfidenceText(
            row.incrementalSalesLift,
            row.liftConfidenceLevel
          )
        : MISSING_VALUE,
  },
  {
    field: 'projectedIncrementalSales',
    headerName: 'Proj. Incr. Sales',
    display: 'flex',
    flex: 1,
    minWidth: 150,
    type: 'number',
    valueFormatter: (value, row) =>
      row.statisticallySignificant
        ? formatters.currency(value, { decimals: 1 })
        : MISSING_VALUE,
  },
];
